import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import {
  CreateSubscriptionPlanRequest,
  SubscriptionPlanResponse,
  SubscriptionPlansResponse,
  ReferralBonusRulesResponse,
  ReferralBonusRuleResponse,
  CreateReferralBonusRuleRequest,
  RetentionReportParams,
} from "./types";

export const adminSubscriptionsApi = createApi({
  reducerPath: "adminSubscriptionsApi",
  baseQuery: baseApiQuery,
  tagTypes: ["SubscriptionPlans", "ReferralBonusRules"],
  endpoints: (builder) => ({
    // Get all subscription plans
    getSubscriptionPlans: builder.query<SubscriptionPlansResponse, void>({
      query: () => `/admin/subscriptions/plans`,
      providesTags: ["SubscriptionPlans"],
    }),

    // Create subscription plan
    createSubscriptionPlan: builder.mutation<
      SubscriptionPlanResponse,
      CreateSubscriptionPlanRequest
    >({
      query: (planData) => ({
        url: `/admin/subscriptions/plans`,
        method: "POST",
        body: planData,
      }),
      invalidatesTags: ["SubscriptionPlans"],
    }),

    // Update subscription plan
    updateSubscriptionPlan: builder.mutation<
      SubscriptionPlanResponse,
      { id: number; data: CreateSubscriptionPlanRequest }
    >({
      query: ({ id, data }) => ({
        url: `/admin/subscriptions/plans/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["SubscriptionPlans"],
    }),

    // Delete subscription plan
    deleteSubscriptionPlan: builder.mutation<{ result: boolean }, number>({
      query: (id) => ({
        url: `/admin/subscriptions/plans/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SubscriptionPlans"],
    }),

    // Get all referral bonus rules
    getReferralBonusRules: builder.query<ReferralBonusRulesResponse, void>({
      query: () => `/admin/subscriptions/referral/bonus/rules`,
      providesTags: ["ReferralBonusRules"],
    }),

    // Create referral bonus rule
    createReferralBonusRule: builder.mutation<
      ReferralBonusRuleResponse,
      CreateReferralBonusRuleRequest
    >({
      query: (ruleData) => ({
        url: `/admin/subscriptions/referral/bonus/rules`,
        method: "POST",
        body: ruleData,
      }),
      invalidatesTags: ["ReferralBonusRules"],
    }),

    // Delete referral bonus rule
    deleteReferralBonusRule: builder.mutation<{ result: boolean }, number>({
      query: (id) => ({
        url: `/admin/subscriptions/referral/bonus/rules/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ReferralBonusRules"],
    }),

    // Get retention report
    getSubscriptionRetentionReport: builder.query<
      Blob,
      RetentionReportParams
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();

        if (params.cities?.length) {
          params.cities.forEach((city) =>
            queryParams.append("cities", city.toString())
          );
        }
        if (params.calibers?.length) {
          params.calibers.forEach((caliber) =>
            queryParams.append("calibers", caliber)
          );
        }
        if (params.lessThanDays !== undefined) {
          queryParams.append("lessThanDays", params.lessThanDays.toString());
        }
        if (params.inviters?.length) {
          params.inviters.forEach((inviter) =>
            queryParams.append("inviters", inviter)
          );
        }
        return {
          url: `/admin/subscriptions/reports/retention/xlsx${
            queryParams.toString() ? `?${queryParams.toString()}` : ""
          }`,
          method: "GET",
          responseHandler: async (response) => {
            return response.blob();
          },
        };
      },
    }),
    
  }),
});

export const {
  useGetSubscriptionPlansQuery,
  useCreateSubscriptionPlanMutation,
  useUpdateSubscriptionPlanMutation,
  useDeleteSubscriptionPlanMutation,
  useGetReferralBonusRulesQuery,
  useCreateReferralBonusRuleMutation,
  useDeleteReferralBonusRuleMutation,
  useLazyGetSubscriptionRetentionReportQuery,
} = adminSubscriptionsApi;
